import { useEffect, useState, type ChangeEvent, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import api, { ApiError } from '../lib/apiClient';
import SearchableSelect from '../components/SearchableSelect';
import { listOptions, useConfig } from '../context/ConfigContext';

interface EmployeeOption {
  id: number;
  full_name: string;
  [key: string]: unknown;
}

// TS port of the source (Falah) app's pages/AssetForm.jsx. Matches
// AssetController::validated() exactly.
interface AssetFormState {
  description: string;
  quantity: string;
  asset_type: string;
  category: string;
  department: string;
  level: string;
  location: string;
  asset_tag: string;
  serial_number: string;
  purchase_value: string;
  purchase_date: string;
  warranty: string;
  vendor: string;
  brand: string;
  model_number: string;
  notes: string;
  useful_life_years: string;
  residual_value: string;
  custodian_employee_id: string | number;
}

const emptyForm: AssetFormState = {
  description: '',
  quantity: '1',
  asset_type: 'non_fixed',
  category: '',
  department: '',
  level: '',
  location: '',
  asset_tag: '',
  serial_number: '',
  purchase_value: '',
  purchase_date: '',
  warranty: '',
  vendor: '',
  brand: '',
  model_number: '',
  notes: '',
  useful_life_years: '',
  residual_value: '',
  custodian_employee_id: '',
};

interface AssetRecord {
  description: string;
  quantity: number | null;
  asset_type: string;
  category: string | null;
  department: string | null;
  level: string | null;
  location: string | null;
  asset_tag: string | null;
  serial_number: string | null;
  purchase_value: number | null;
  purchase_date: string | null;
  warranty: string | null;
  vendor: string | null;
  brand: string | null;
  model_number: string | null;
  notes: string | null;
  useful_life_years: number | null;
  residual_value: number | null;
  custodian_employee_id: number | null;
}

type FormErrors = Record<string, string[]>;

export function AssetForm() {
  const { lists, settings } = useConfig();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isNew = !id;
  const [form, setForm] = useState<AssetFormState>(emptyForm);
  const [errors, setErrors] = useState<FormErrors>({});
  const [loaded, setLoaded] = useState(isNew);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isNew) return;
    api.get<{ asset_tag: string }>('/assets/next-tag').then((res) => {
      setForm((f) => ({ ...f, asset_tag: res.data.asset_tag }));
    });
  }, [isNew]);

  useEffect(() => {
    if (isNew) return;
    api.get<AssetRecord>(`/assets/${id}`).then((res) => {
      const asset = res.data;
      setForm({
        description: asset.description,
        quantity: asset.quantity != null ? String(asset.quantity) : '',
        asset_type: asset.asset_type,
        category: asset.category ?? '',
        department: asset.department ?? '',
        level: asset.level ?? '',
        location: asset.location ?? '',
        asset_tag: asset.asset_tag ?? '',
        serial_number: asset.serial_number ?? '',
        purchase_value: asset.purchase_value != null ? String(asset.purchase_value) : '',
        purchase_date: asset.purchase_date ? asset.purchase_date.slice(0, 10) : '',
        warranty: asset.warranty ?? '',
        vendor: asset.vendor ?? '',
        brand: asset.brand ?? '',
        model_number: asset.model_number ?? '',
        notes: asset.notes ?? '',
        useful_life_years: asset.useful_life_years != null ? String(asset.useful_life_years) : '',
        residual_value: asset.residual_value != null ? String(asset.residual_value) : '',
        custodian_employee_id: asset.custodian_employee_id ?? '',
      });
      setLoaded(true);
    });
  }, [id, isNew]);

  const set =
    (key: keyof AssetFormState) =>
    (e: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
      setForm({ ...form, [key]: e.target.value });

  const save = async (e: FormEvent) => {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    const payload: Record<string, unknown> = Object.fromEntries(
      Object.entries(form).map(([k, v]) => [k, v === '' ? null : v])
    );
    try {
      if (isNew) await api.post('/assets', payload);
      else await api.put(`/assets/${id}`, payload);
      navigate('/resources/assets');
    } catch (err) {
      setErrors(
        err instanceof ApiError && err.errors ? err.errors : { general: [err instanceof ApiError ? err.message : 'Save failed.'] }
      );
    } finally {
      setSaving(false);
    }
  };

  if (!loaded) return null;

  return (
    <>
      <header className="page-header">
        <h1>{isNew ? 'Add asset' : 'Edit asset'}</h1>
      </header>

      <div className="card">
        <form onSubmit={save} className="form-grid">
          {errors.general && <div className="alert alert-error">{errors.general[0]}</div>}
          <label>
            Asset Tag
            <input value={form.asset_tag} onChange={set('asset_tag')} placeholder="e.g. EO-2-191205-0001(A)" />
            <small className="muted">Auto-generated for new assets — edit if this asset already has a physical tag.</small>
          </label>
          <label>
            Asset Type *
            <select value={form.asset_type} onChange={set('asset_type')} required>
              <option value="fixed">Fixed</option>
              <option value="non_fixed">Non-Fixed</option>
            </select>
          </label>
          <label>
            Quantity
            <input type="number" min="1" value={form.quantity} onChange={set('quantity')} />
          </label>
          <label>
            Category
            <select value={form.category} onChange={set('category')}>
              <option value="">—</option>
              {listOptions(lists, 'asset_category').map((item) => (
                <option key={item.value} value={item.value}>
                  {item.label}
                </option>
              ))}
            </select>
          </label>
          <label>
            Department
            <select value={form.department} onChange={set('department')}>
              <option value="">—</option>
              {listOptions(lists, 'asset_department').map((item) => (
                <option key={item.value} value={item.value}>
                  {item.label}
                </option>
              ))}
            </select>
          </label>
          {form.asset_type === 'fixed' && (
            <>
              <label>
                Useful Life (Years)
                <input
                  type="number"
                  min="1"
                  value={form.useful_life_years}
                  onChange={set('useful_life_years')}
                />
                {errors.useful_life_years && <small className="error">{errors.useful_life_years[0]}</small>}
              </label>
              <label>
                Residual Value ({settings.currency})
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.residual_value}
                  onChange={set('residual_value')}
                />
                {errors.residual_value && <small className="error">{errors.residual_value[0]}</small>}
              </label>
              <label>
                Custodian
                <SearchableSelect<EmployeeOption>
                  endpoint="/employees"
                  value={form.custodian_employee_id}
                  onChange={(id) => setForm({ ...form, custodian_employee_id: id })}
                  getLabel={(e) => e.full_name}
                  placeholder="Search employees…"
                />
                {errors.custodian_employee_id && <small className="error">{errors.custodian_employee_id[0]}</small>}
              </label>
            </>
          )}
          <label>
            Level
            <input value={form.level} onChange={set('level')} placeholder="e.g. 2, All" />
          </label>
          <label>
            Location
            <input value={form.location} onChange={set('location')} />
          </label>
          <label>
            Serial Number
            <input value={form.serial_number} onChange={set('serial_number')} />
          </label>
          <label>
            Purchase Value ({settings.currency})
            <input type="number" step="0.01" min="0" value={form.purchase_value} onChange={set('purchase_value')} />
            {errors.purchase_value && <small className="error">{errors.purchase_value[0]}</small>}
          </label>
          <label>
            Purchase Date
            <input type="date" value={form.purchase_date} onChange={set('purchase_date')} />
          </label>
          <label>
            Warranty
            <input value={form.warranty} onChange={set('warranty')} placeholder="e.g. 1 Year" />
          </label>
          <label>
            Vendor
            <input value={form.vendor} onChange={set('vendor')} />
          </label>
          <label>
            Brand
            <input value={form.brand} onChange={set('brand')} />
          </label>
          <label>
            Model Number
            <input value={form.model_number} onChange={set('model_number')} />
          </label>
          <label className="span-2">
            Description *
            <textarea rows={2} value={form.description} onChange={set('description')} required />
            {errors.description && <small className="error">{errors.description[0]}</small>}
          </label>
          <label className="span-2">
            Notes
            <textarea rows={2} value={form.notes} onChange={set('notes')} />
          </label>
          <div className="form-actions span-2">
            <Link className="btn btn-ghost" to="/resources/assets">
              Cancel
            </Link>
            <button className="btn btn-primary" disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </>
  );
}
